import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Suspense, createContext, lazy, useRef, useState } from "react";
import { ConfirmDialogProvider } from "./components/admin-ui/ConfirmDialog";
import { Toaster } from "sonner";
import AdminRoute from "./components/AdminRoute";
import DeviceGate from "./components/DeviceGate";
import EnrollPage from "./pages/EnrollPage";
import Layout from "./components/Layout";
import { MatterProvider } from "./MatterProvider";
import StoreAdminLoginPage from "./components/StoreAdminLoginPage";
import StoreAdminPendingPage from "./components/StoreAdminPendingPage";
import StoreAdminRoute from "./components/StoreAdminRoute";
import StoreAdminSignupPage from "./components/StoreAdminSignupPage";
import { LanguageProvider } from "./contexts/LanguageContext";

const MainPage = lazy(() => import("./pages/MainPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DisplayPage = lazy(() => import("./pages/DisplayPage"));
const DisplayGalleryPage = lazy(() => import("./pages/GalleryPage"));
const StoreAdminLiquidListPage = lazy(() => import("./pages/StoreAdminLiquidListPage"));
const StoreAdminSlidesPage = lazy(() => import("./pages/StoreAdminSlidesPage"));
const StoreAdminDashboardPage = lazy(() => import("./pages/StoreAdminDashboardPage"));
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminStoreAdminsPage = lazy(() => import("./components/AdminStoreAdminsPage"));
const AdminLiquidsPage = lazy(() => import("./components/AdminLiquidsPage"));
const AdminDisplayImagesPage = lazy(() => import("./pages/AdminDisplayImagesPage"));
const AdminStartImagesPage = lazy(() => import("./pages/AdminStartImagesPage"));
const AdminManagementPage = lazy(() => import("./pages/AdminManagementPage"));
const StoreAdminLayout = lazy(() => import("./components/StoreAdminLayout"));

export const FormDataContext = createContext();

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#2b2b2b] text-[#f2f2f2]">
      <div className="text-lg">화면을 불러오는 중...</div>
    </div>
  );
}

function AdminFallback() {
  return (
    <div className="admin-scope dark min-h-screen flex items-center justify-center bg-background" />
  );
}

function MainRoute() {
  const blenderRef = useRef(null);
  const [currentPage, setCurrentPage] = useState("start");

  return (
    <MatterProvider
      canvasTargetRef={blenderRef}
      disableWalls={currentPage === "recommend"}
    >
      <DeviceGate>
        <MainPage setCurrentPage={setCurrentPage} />
      </DeviceGate>
    </MatterProvider>
  );
}

function App() {
  const [formData, setFormData] = useState({
    age: null,
    gender: null,
    menthol: null,
    sweetness: null,
    strongness: null,
    style: [],
    category: [],
    fruits: [],
  });

  const resetFormData = () => {
    setFormData({
      age: null,
      gender: null,
      menthol: null,
      sweetness: null,
      strongness: null,
      style: [],
      category: [],
      fruits: [],
    });
  };

  const [cartSessions, setCartSessions] = useState([]); // Array<CartSession>
  const [currentPickedItems, setCurrentPickedItems] = useState([]); // Array<RecoItem>
  const [currentRecommendLogId, setCurrentRecommendLogId] = useState(null);
  const cartSessionSeq = useRef(0);

  // 카트에 세션 추가 (최대 3개)
  const addCartSession = (session) => {
    const id = ++cartSessionSeq.current;
    setCartSessions((prev) => (prev.length >= 3 ? prev : [...prev, { ...session, id }]));
  };

  // 카트에서 특정 아이템 제거 (세션 id + brand+name 기준)
  const removeCartItem = (sessionId, brand, name) => {
    setCartSessions((prev) =>
      prev
        .map((s) =>
          s.id === sessionId
            ? { ...s, pickedRecoItems: s.pickedRecoItems.filter((item) => !(item.brand === brand && item.name === name)) }
            : s
        )
        .filter((s) => s.pickedRecoItems.length > 0)
    );
  };

  // 전체 카트 초기화
  const clearCart = () => {
    setCartSessions([]);
    setCurrentPickedItems([]);
    setCurrentRecommendLogId(null);
  };

  // 취향(설문값)만 초기화, 나이/성별 유지 (세션B 시작 시 사용)
  const resetSurveyKeepUserInfo = () => {
    setFormData((prev) => ({
      ...prev,
      menthol: null,
      sweetness: null,
      strongness: null,
      style: [],
      category: [],
      fruits: [],
    }));
  };

  return (
    <LanguageProvider>
      <FormDataContext.Provider
        value={{
          formData, setFormData, resetFormData,
          cartSessions, addCartSession, removeCartItem, clearCart,
          currentPickedItems, setCurrentPickedItems,
          currentRecommendLogId, setCurrentRecommendLogId,
          resetSurveyKeepUserInfo,
        }}
      >
        <ConfirmDialogProvider />
        <Toaster position="bottom-right" theme="dark" />
        <BrowserRouter>
          <Layout>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<MainRoute />} />
                <Route path="/enroll" element={<EnrollPage />} />

                <Route
                  path="/gallery"
                  element={
                    <DeviceGate>
                      <DisplayGalleryPage />
                    </DeviceGate>
                  }
                />
                <Route
                  path="/gallery/display"
                  element={
                    <DeviceGate>
                      <DisplayPage />
                    </DeviceGate>
                  }
                />

                <Route path="/admin/login" element={<LoginPage />} />
                <Route
                  path="/admin/*"
                  element={
                    <Suspense fallback={<AdminFallback />}>
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    </Suspense>
                  }
                >
                  <Route index element={null} />
                  <Route path="algo-health" element={null} />
                  <Route path="store-admins" element={<AdminStoreAdminsPage />} />
                  <Route path="liquids" element={<AdminLiquidsPage />} />
                  <Route path="start-images" element={<AdminStartImagesPage />} />
                  <Route path="display-images" element={<AdminDisplayImagesPage />} />
                  <Route path="admins" element={<AdminManagementPage />} />
                </Route>

                <Route path="/store-admin/login" element={<StoreAdminLoginPage />} />
                <Route path="/store-admin/signup" element={<StoreAdminSignupPage />} />
                <Route path="/store-admin/pending" element={<StoreAdminPendingPage />} />
                <Route
                  path="/store-admin/*"
                  element={
                    <Suspense fallback={<AdminFallback />}>
                      <StoreAdminRoute>
                        <StoreAdminLayout />
                      </StoreAdminRoute>
                    </Suspense>
                  }
                >
                  <Route index element={<StoreAdminDashboardPage />} />
                  <Route path="liquids" element={<StoreAdminLiquidListPage />} />
                  <Route path="slides" element={<StoreAdminSlidesPage />} />
                </Route>
              </Routes>
            </Suspense>
          </Layout>
        </BrowserRouter>
      </FormDataContext.Provider>
    </LanguageProvider>
  );
}

export default App;
